import { useTranslation } from 'react-i18next';

export interface Product {
  id: number;
  name: string;
  category: string;
  brand: string;
  brandLogo?: string;
  image: string;
  inStock: boolean;
  featured?: boolean;
}

interface ProductCardProps {
  product: Product;
  onRequestQuote: (name: string) => void;
  size?: 'normal' | 'large';
}

const categoryColors: Record<string, string> = {
  engine: 'bg-amber-100 text-amber-700',
  braking: 'bg-red-100 text-red-700',
  suspension: 'bg-emerald-100 text-emerald-700',
  lighting: 'bg-sky-100 text-sky-700',
  trailer: 'bg-violet-100 text-violet-700',
};

export default function ProductCard({ product, onRequestQuote, size = 'normal' }: ProductCardProps) {
  const { t } = useTranslation('common');
  const colorClass = categoryColors[product.category] || 'bg-gray-100 text-gray-700';
  const isLarge = size === 'large';

  const categoryLabel = (category: string): string => {
    const map: Record<string, string> = {
      engine: t('products.cat.engine'),
      braking: t('products.cat.braking'),
      suspension: t('products.cat.suspension'),
      lighting: t('products.cat.lighting'),
      trailer: t('products.cat.trailer'),
    };
    return map[category] || category;
  };

  return (
    <div
      className={`bg-white rounded-xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 border border-gray-100 flex flex-col cursor-pointer ${
        isLarge ? 'h-full' : ''
      }`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden flex-shrink-0 ${isLarge ? 'h-44 sm:h-64' : 'h-36 sm:h-44 md:h-52'}`}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {/* Category Badge */}
        <span
          className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass}`}
        >
          {categoryLabel(product.category)}
        </span>
        {/* Out of stock overlay */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              {t('products.outOfStock')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        {/* Brand */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-medal-line text-teal-600 text-sm"></i>
          </div>
          <span className="text-xs font-medium text-teal-700 uppercase tracking-wide">
            {product.brand}
          </span>
        </div>

        {/* Name */}
        <h3 className={`font-bold text-gray-900 leading-snug flex-1 mb-3 ${isLarge ? 'text-base sm:text-lg' : 'text-xs sm:text-sm'}`}>
          {product.name}
        </h3>

        {/* CTA */}
        <button
          onClick={() => product.inStock && onRequestQuote(product.name)}
          disabled={!product.inStock}
          className={`w-full py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
            product.inStock
              ? 'bg-teal-600 text-white hover:bg-teal-700 active:scale-95'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {product.inStock ? t('products.requestQuote') : t('products.notifyMe')}
        </button>
      </div>
    </div>
  );
}
